// ./src/components/generic/EmptyState/EmptyState.tsx
import React from "react";
import { Stack, Text, Title, Center, MantineSize } from "@mantine/core";

import { AppIcon } from "../AppIcon";
import { AppButton } from "../AppButton";

export interface EmptyStateProps {
  /** Main title/message */
  title: string;

  /** Optional description text */
  description?: string | undefined;

  /** Icon to display (without 'Icon' prefix) */
  iconName?: string | undefined;

  /** Size of the empty state */
  size?: "sm" | "md" | "lg" | undefined;

  /** Optional action button */
  actionButton?: React.ReactNode;

  /** Quick action button props - if provided, creates a primary button */
  actionLabel?: string;
  actionIcon?: string;
  onAction?: () => void;
  actionInProgress?: boolean;

  /** Additional className for styling */
  className?: string;

  /** Additional styles */
  style?: React.CSSProperties;

  display?: boolean | undefined;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  title,
  description,
  iconName = "Search",
  size = "md",
  actionButton,
  actionLabel,
  actionIcon,
  onAction,
  actionInProgress = false,
  className,
  style,
  display,
}) => {
  // Get sizes based on size prop
  const getSizes = () => {
    switch (size) {
      case "sm":
        return {
          iconSize: 48,
          titleOrder: 4,
          spacing: "sm",
          textSize: "sm",
        };
      case "md":
        return {
          iconSize: 64,
          titleOrder: 3,
          spacing: "md",
          textSize: "md",
        };
      case "lg":
        return {
          iconSize: 80,
          titleOrder: 2,
          spacing: "lg",
          textSize: "lg",
        };
      default:
        return {
          iconSize: 64,
          titleOrder: 3,
          spacing: "md",
          textSize: "md",
        };
    }
  };

  const sizes = getSizes();

  // Render action button
  const renderActionButton = () => {
    if (actionButton) {
      return actionButton;
    }

    if (actionLabel && onAction) {
      return (
        <AppButton
          iconName={actionIcon}
          inProgress={actionInProgress}
          onClick={onAction}
        >
          {actionLabel}
        </AppButton>
      );
    }

    return null;
  };

  if (display === false || (display != null && display !== true)) {
    return null;
  }

  return (
    <Center {...(className && { className })} style={style}>
      <Stack align="center" gap={sizes.spacing} ta="center">
        <AppIcon iconName={iconName} size={sizes.iconSize} color="black" />

        <Stack align="center" gap="xs">
          <Title order={sizes.titleOrder as 1 | 2 | 3 | 4 | 5 | 6} c="gray.7">
            {title}
          </Title>

          {description && (
            <Text size={sizes.textSize as MantineSize} c="gray.6" maw={600}>
              {description}
            </Text>
          )}
        </Stack>

        {renderActionButton()}
      </Stack>
    </Center>
  );
};
